import { computed, h, defineComponent, ref, watch } from 'vue';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { renderAnalyticsTeaserPage } from '@/ui/pages/teasers/AnalyticsTeaserPage';
import { renderForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
import { col, cols } from '@/ui/components';
import LineChartCard from '@/_legacy/views/Analytics/components/LineChartCard.vue';
import { t } from '@/ui/i18n/i18n';
import {
  redirectIssues,
  redirectNegativeResponses,
  redirectOverdueInspections,
  redirectScores,
  redirectSubmittedForms,
} from '@/ui/pages/analytics/Overview/redirects';
import {
  emptyGroupedInspectionList,
  emptyGroupedIssueList,
  emptyGroupedQuestionList,
  GroupedInspectionList,
  GroupedIssueList,
  GroupedQuestionList,
} from '@/entities/analytics';
import { useTask } from '@/ui/concerns';
import { sequence } from '@/ui/helpers/uiHelper';
import {
  groupedGeneralInspectionList,
  groupedInspectionList,
  groupedIssueList,
  groupedQuestionList,
} from '@/services/api/resources/analytics';
import { useQueryFiltering } from '@/ui/pages/analytics/Overview/useQueryFilters';
import { useQueries } from '@/ui/pages/analytics/Overview/useQueries';
import { datesToDuration } from '@/ui/helpers/dateHelper';
import { tooltip } from '@/ui/components/Tooltip';
import { featureExplanationIcon } from '@/ui/components/icon/icons';

export const OverviewCardsRow = defineComponent({
  name: 'OverviewCardsRow',
  setup() {
    if (!hasFeature('analytics')) {
      return renderAnalyticsTeaserPage;
    }

    if (!hasPermission(Permissions.VIEW_ALL_ANALYTICS)) {
      return renderForbiddenPage;
    }

    const isCardLoading = ref(true);

    const queryFilters = useQueryFiltering();

    const {
      group,
      entityItem,
      user,
      category,
      checklist,
      questionTitle,
      response,
      section,
      inspection,
      status,
      type,
      priority,
      sensor,
      question,
      entityFolder,
    } = queryFilters;

    const {
      baseQuery,
      issuesQuery,
      scoresQuery,
      negativeResponseCardQuery,
      inspectionsOverdueQuery,
      inspectionsQuery,
    } = useQueries();

    const countInspectionsCard = ref<GroupedInspectionList>(emptyGroupedInspectionList);
    const overdueInspectionsCardList = ref<GroupedInspectionList>(emptyGroupedInspectionList);
    const negativeResponsesCardList = ref<GroupedQuestionList>(emptyGroupedQuestionList);
    const scoresCardList = ref<GroupedQuestionList>(emptyGroupedQuestionList);
    const issuesCardList = ref<GroupedIssueList>(emptyGroupedIssueList);

    const overdueToNormalPercent = computed(() => {
      const allInspectionsCount = countInspectionsCard.value.meta.inspectionCount;
      const overdueInspectionsCount = overdueInspectionsCardList.value.meta.inspectionCount;

      // all of them
      if (allInspectionsCount === 0) {
        return 0;
      }

      const rawPercentage = overdueInspectionsCount / allInspectionsCount;
      return Number(Math.abs(rawPercentage).toFixed(2));
    });

    const getCardList = useTask(
      () => {
        isCardLoading.value = true;
        const cardsGroupBy = datesToDuration(baseQuery().start, baseQuery().end);

        return sequence(
          groupedGeneralInspectionList(cardsGroupBy, inspectionsQuery()),
          groupedInspectionList(cardsGroupBy, inspectionsOverdueQuery()),
          groupedQuestionList(cardsGroupBy, negativeResponseCardQuery()),
          groupedQuestionList(cardsGroupBy, scoresQuery()),
          groupedIssueList(cardsGroupBy, issuesQuery()),
        );
      },
      ([
        inspectionsRes,
        inspectionsOverdueRes,
        negativeResponsesRes,
        scoresRes,
        issuesRes,
      ]) => {
        countInspectionsCard.value = inspectionsRes;
        overdueInspectionsCardList.value = inspectionsOverdueRes;
        negativeResponsesCardList.value = negativeResponsesRes;
        scoresCardList.value = scoresRes;
        issuesCardList.value = issuesRes;

        isCardLoading.value = false;
      },
    );

    watch([
      entityItem,
      group,
      user,
      category,
      checklist,
      questionTitle,
      response,
      section,
      inspection,
      status,
      type,
      priority,
      sensor,
      question,
      entityFolder,
    ], () => {
      getCardList.run();
    });

    getCardList.run();

    return function render() {
      return cols([
        col([
          h(LineChartCard, {
            props: {
              loading: isCardLoading.value,
              chartData: { series: [countInspectionsCard.value.data.map(row => row.inspectionCount)] },
              // inspectionCount includes overdue count from overdueInspectionCount already
              // inspectionCount - all inspections regardless of Overdue
              // not overdue = inspectionCount - overdueInspectionCount
              number: countInspectionsCard.value.meta.inspectionCount,
              label: t('inspections.count'),
              color: 'red',
            },
            on: {
              labelClick: () => redirectSubmittedForms(inspectionsQuery()),
              numberClick: () => redirectSubmittedForms(inspectionsQuery()),
              chartClick: () => redirectSubmittedForms(inspectionsQuery()),
            },
          }),
        ], false, { 'is-one-fifth': true }),
        col([
          h(LineChartCard, {
            props: {
              loading: isCardLoading.value,
              chartData: { series: [negativeResponsesCardList.value.data.map(row => row.negativeResponseCount)] },
              number: negativeResponsesCardList.value.meta.negativeResponseCount,
              percentage: negativeResponsesCardList.value.meta.negativeResponsePercentage,
              label: t('analytics.negative-resp-count'),
              color: 'orange',
            },
            on: {
              labelClick: () => redirectNegativeResponses(negativeResponseCardQuery()),
              numberClick: () => redirectNegativeResponses(negativeResponseCardQuery()),
              chartClick: () => redirectNegativeResponses(negativeResponseCardQuery()),
            },
          }, [
            h('div', { slot: 'label', class: 'page-title-tooltiped' }, [
              t('analytics.negative-resp-count'),
              tooltip({
                label: t('feature-explanation.negative-responses'),
                multilined: true,
                position: 'is-bottom',
              }, [featureExplanationIcon({ classes: 'ml-1' })], 'label'),
            ]),
          ]),
        ], false, { 'is-one-fifth': true }),
        col([
          h(LineChartCard, {
            props: {
              loading: isCardLoading.value,
              chartData: { series: [scoresCardList.value.data.map(row => row.scorePercentage)] },
              percentage: scoresCardList.value.meta.scorePercentage,
              label: t('analytics.score-count'),
              color: 'green',
            },
            on: {
              labelClick: () => redirectScores(scoresQuery()),
              numberClick: () => redirectScores(scoresQuery()),
              chartClick: () => redirectScores(scoresQuery()),
            },
          }, [
            h('div', { slot: 'label', class: 'page-title-tooltiped' }, [
              t('analytics.score-count'),
              tooltip({
                label: t('feature-explanation.scores'),
                multilined: true,
              }, [featureExplanationIcon({ classes: 'ml-1' })], 'label'),
            ]),
          ]),
        ], false, { 'is-one-fifth': true }),
        col([
          h(LineChartCard, {
            props: {
              loading: isCardLoading.value,
              chartData: {
                series: [overdueInspectionsCardList.value.data.map(row => row.overdueInspectionCount)],
              },
              number: overdueInspectionsCardList.value.meta.overdueInspectionCount,
              percentage: overdueToNormalPercent.value,
              label: t('analytics.overdue-count'),
              color: 'blue',
            },
            on: {
              labelClick: () => redirectOverdueInspections(inspectionsOverdueQuery()),
              numberClick: () => redirectOverdueInspections(inspectionsOverdueQuery()),
              chartClick: () => redirectOverdueInspections(inspectionsOverdueQuery()),
            },
          }),
        ], false, { 'is-one-fifth': true }),
        col([
          h(LineChartCard, {
            props: {
              loading: isCardLoading.value,
              chartData: { series: [issuesCardList.value.data.map(row => row.issueCount)] },
              number: issuesCardList.value.meta.issueCount,
              label: t('issues.count'),
              color: 'purple',
            },
            on: {
              labelClick: () => redirectIssues(issuesQuery()),
              numberClick: () => redirectIssues(issuesQuery()),
              chartClick: () => redirectIssues(issuesQuery()),
            },
          }),
        ], false, { 'is-one-fifth': true }),
      ]);
    };
  },
});
